import { Action, action, Thunk, thunk, ThunkOn, thunkOn, State } from 'easy-peasy';
import { StoreState } from './store';
import { getDefaultSubtitleTrack, getTextTracks } from '../utils/tracks';
import { VideoItem, SubtitleTextSizes, SubtitleTextWeights, SubtitleTextColors } from '../typings';
import { Browser } from '../utils/browser';

export interface SubtitlesState {
  activeSubtitle: TextTrack | null;
  webVttSubtitles: TextTrack[];
  subtitleTextSize: SubtitleTextSizes;
  subtitleTextWeight: SubtitleTextWeights;
  subtitleTextColor: SubtitleTextColors;
  reset: Thunk<SubtitlesState>;
  setActiveSubtitleState: Action<SubtitlesState, TextTrack | null>;
  setActiveSubtitle: Thunk<SubtitlesState, TextTrack | null, any, StoreState>;
  setWebVttSubtitles: Thunk<SubtitlesState, TextTrack[]>;
  setSubtitleTextSize: Action<SubtitlesState, SubtitleTextSizes>;
  setSubtitleTextWeight: Action<SubtitlesState, SubtitleTextWeights>;
  setSubtitleTextColor: Action<SubtitlesState, SubtitleTextColors>;
  loadSubtitlesFromVideojs: Thunk<SubtitlesState, TextTrackList>;
  loadDefaultSubtitle: Thunk<SubtitlesState, void, any, StoreState>;
  syncSubtitleTimings: Thunk<SubtitlesState, void, any, StoreState>;
  onVideoLoad: ThunkOn<SubtitlesState, VideoItem, StoreState>;
  onAudioTrackChanged: ThunkOn<SubtitlesState, AudioTrack, StoreState>;
  onPlayerDestroy: ThunkOn<SubtitlesState, AudioTrack, StoreState>;
}

const initialState: State<SubtitlesState> = {
  activeSubtitle: null,
  webVttSubtitles: [],
  subtitleTextSize: SubtitleTextSizes.Normal,
  subtitleTextWeight: SubtitleTextWeights.Regular,
  subtitleTextColor: SubtitleTextColors.White,
};

export const subtitlesState: SubtitlesState = {
  ...initialState,

  reset: thunk((actions, webVTTSubtitles, { getState }) => {
    const state = getState();
    Object.assign(state, { ...initialState });
  }),

  setActiveSubtitleState: action((state, activeSubtitle) => {
    state.activeSubtitle = activeSubtitle;
  }),

  setActiveSubtitle: thunk((actions, activeSubtitle, { getStoreState }) => {
    const storeState = getStoreState();

    actions.setActiveSubtitleState(activeSubtitle);

    if (storeState.player.videoEngine) {
      Array.from(getTextTracks(storeState.player.videoEngine)).forEach(textTrack => {
        textTrack.mode = textTrack.label === activeSubtitle?.label ? 'showing' : 'disabled';
      });
    }
  }),

  setWebVttSubtitles: thunk((_, webVTTSubtitles, { getState }) => {
    const state = getState();
    state.webVttSubtitles = webVTTSubtitles;
  }),

  setSubtitleTextSize: action((state, textSize) => {
    state.subtitleTextSize = textSize;
  }),

  setSubtitleTextWeight: action((state, textWeight) => {
    state.subtitleTextWeight = textWeight;
  }),

  setSubtitleTextColor: action((state, textColor) => {
    state.subtitleTextColor = textColor;
  }),

  loadSubtitlesFromVideojs: thunk((actions, textTracks) => {
    const webVttSubtitles: TextTrack[] = [];

    Array.from(textTracks).forEach((textTrack: TextTrack | undefined, index: number) => {
      if (textTrack) {
        textTrack.mode = 'disabled';

        const isWebVttTextTrack = !!textTrack.language;

        if (isWebVttTextTrack && ['subtitles', 'captions'].includes(textTrack.kind)) {
          webVttSubtitles.push(textTrack);
        }
      }
    });

    actions.setWebVttSubtitles(webVttSubtitles);
    actions.loadDefaultSubtitle();
  }),

  loadDefaultSubtitle: thunk((actions, _, { getState, getStoreState }) => {
    const storeState = getStoreState();
    const defaultSubtitle = getDefaultSubtitleTrack(storeState);
    if (defaultSubtitle) actions.setActiveSubtitle(defaultSubtitle);
  }),

  // Sync WebVTT subtitle cue timings when Video.js parses them in the past
  syncSubtitleTimings: thunk((actions, _, { getState, getStoreState }) => {
    const storeState = getStoreState();
    const hasActiveWebVttSubtitle = storeState.subtitles.activeSubtitle?.kind === 'subtitles';

    if (!storeState.player.videoEngine || !hasActiveWebVttSubtitle || Browser.isSafari || Browser.isIOS) return;

    const playlistController = storeState.player.videoEngine.tech_?.vhs?.playlistController_;
    const timeline = (playlistController?.syncController_?.timelines || [])[0];

    Array.from(storeState.subtitles.activeSubtitle!.cues!).forEach(cue => {
      if (cue.startTime < 0) {
        cue.startTime = Math.abs(timeline.mapping - cue.startTime);
        cue.endTime = Math.abs(timeline.mapping - cue.endTime);
      }
    });
  }),

  // Reset subtitle tracks when a video is loaded/changed
  onVideoLoad: thunkOn(
    function observer(actions, storeActions) {
      return [storeActions.player.loadVideo.startType];
    },
    function handler(actions) {
      actions.setActiveSubtitle(initialState.activeSubtitle);
      actions.setWebVttSubtitles(initialState.webVttSubtitles);
    }
  ),

  /**
   * Safari selects a textTrack when the audioTrack is changed.
   * We need to reset the previosly selected text track.
   */
  onAudioTrackChanged: thunkOn(
    function observer(actions, storeActions) {
      return [storeActions.player.setAudioTrack];
    },
    function handler(actions, _, { getState, getStoreState }) {
      const {
        player: { videoElement },
      } = getStoreState();

      if (!videoElement) return;

      const state = getState();
      actions.setActiveSubtitle(state.activeSubtitle);
    }
  ),

  onPlayerDestroy: thunkOn(
    function observer(actions, storeActions) {
      return [storeActions.player.destroy];
    },
    function handler(actions) {
      actions.reset();
    }
  ),
};
